import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaLockOpen } from "react-icons/fa";
import TablePagesTitle from "../../../components/table-pages-title/TablePagesTitle";
import AccessPageAction from "../page-action/AccessPageAction";
import { showToast, showAlert } from "../../../utils/utils";
import { Tooltip } from "react-tooltip";
import {
  EMAIL_REGEX_STRICT,
  NAME_REGEX,
  showToastType
} from "../../../utils/constants/appConstants";
import {
  alertActions,
  buttonName,
  createReffPartner,
  createUser,
  editUser,
  profiletexts,
  signup,
  userManage
} from "../../../locales/en";
import {
  getUnlockUserUrl,
  getAllRolesUrl,
  getUserPrimaryDataURL,
  userManagementUsersURL
} from "../../../utils/constants/apiEndPoints";
import privateAxios from "../../../utils/axiosPrivate";
import EditUserSkeleton from "../../../components/skeletons/EditUserSkeleton";
import EditUserRoleSkeleton from "../../../components/skeletons/EditUserRoleSkeleton";
import {
  CREATE_USER,
  DEACTIVATED_USER,
  DELETED_USER,
  ADMIN_ACCESS_USER
} from "../../../route/paths";

const EditUserManage = () => {
  const { id } = useParams();
  const initialUserData = {
    firstName: "",
    middleName: "",
    lastName: "",
    primaryEmail: "",
    isActive: ""
  };
  const [userData, setUserData] = useState(initialUserData);

  const [addroles, setRoles] = useState([]);
  const initialLoading = true;
  const [loading, setLoading] = useState(initialLoading);
  const initialRoleLoading = true;
  const [roleLoading, setRoleLoading] = useState(initialRoleLoading);

  const [nameError, setNameError] = useState("");
  const [middleNameError, setMiddleNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [rolesError, setRolesError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialRender = useRef(true);
  const navigate = useNavigate();

  const fetchUserData = useCallback(async () => {
    try {
      setLoading(true);
      const userResponse = await privateAxios.get(userManagementUsersURL(id));
      const userData = userResponse.data.data;
      setUserData({
        ...userData,
        roles: userData.roles.map((role) => role.id)
      });
    } catch (error) {
      showToast(error.response.data.message, showToastType.error);
    } finally {
      setLoading(false);
    }
  }, [id, privateAxios]);

  const fetchRolesData = useCallback(async () => {
    try {
      setRoleLoading(true);
      const response = await privateAxios.get(getAllRolesUrl);
      setRoles(response.data.data);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        // No roles found, handle as needed (currently ignored)
      } else {
        showToast(error.response.data.message, showToastType.error);
      }
    } finally {
      setRoleLoading(false);
    }
  }, [privateAxios]);

  useEffect(() => {
    if (initialRender.current) {
      if (id) {
        fetchUserData();
        fetchRolesData();
      }
      initialRender.current = false;
    }
  }, [fetchUserData, fetchRolesData, id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "primaryEmail") {
      const emailRegex = EMAIL_REGEX_STRICT;
      if (emailRegex.test(value)) {
        setEmailError("");
      } else {
        setEmailError(createUser.errMsg.item1);
      }
    }

    setUserData({
      ...userData,
      [name]: value
    });
  };

  const handleCheckboxChange = (roleId) => {
    const roles = userData.roles || [];
    const isChecked = roles.includes(roleId);
    const updatedRoles = isChecked
      ? roles.filter((id) => id !== roleId)
      : [...roles, roleId];
    setUserData({
      ...userData,
      roles: updatedRoles
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    let isValid = true;
    setRolesError("");

    if (!userData.firstName) {
      setNameError(createReffPartner.errmsg.item4);
      isValid = false;
    } else {
      setNameError("");
    }
    if (userData.firstName) {
      const isValidName = NAME_REGEX.test(userData.firstName);
      if (isValidName) {
        setNameError("");
      } else {
        setNameError(signup.errors.firstnameErr);
        isValid = false;
      }
    }

    if (!userData.lastName) {
      setLastNameError(editUser.errmsg.item6);
      isValid = false;
    } else {
      setLastNameError("");
    }

    if (userData.lastName) {
      const isValidName = NAME_REGEX.test(userData.lastName);
      if (isValidName) {
        setLastNameError("");
      } else {
        setLastNameError(signup.errors.lastnameErr);
        isValid = false;
      }
    }
    if (userData.middleName) {
      const isValidName = NAME_REGEX.test(userData.middleName);
      if (isValidName) {
        setMiddleNameError("");
      } else {
        setMiddleNameError(editUser.errmsg.item7);
        isValid = false;
      }
    }

    if (!userData.primaryEmail) {
      setEmailError(editUser.errmsg.item8);
      isValid = false;
    } else {
      const emailRegex = EMAIL_REGEX_STRICT;
      if (!emailRegex.test(userData.primaryEmail)) {
        setEmailError(createUser.errMsg.item1);
        isValid = false;
      } else {
        setEmailError("");
      }
    }

    if (userData.roles.length === 0) {
      setRolesError(createUser.errMsg.item5);
      isValid = false;
    }

    if (!isValid) {
      return;
    }

    try {
      setIsSubmitting(true);
      const updatedUserData = {
        firstName: userData.firstName,
        middleName: userData.middleName,
        lastName: userData.lastName,
        primaryEmail: userData.primaryEmail,
        isActive: userData.isActive,
        roleIds: userData.roles
      };
      const response = await privateAxios.put(
        getUserPrimaryDataURL(id),
        updatedUserData
      );
      showToast(response.data.message, showToastType.success);
      navigate(ADMIN_ACCESS_USER);
    } catch (error) {
      if (error.response && error.response.status === 400) {
        const errors = error.response.data.errors;
        if (Array.isArray(errors)) {
          errors.forEach((err) => {
            if (typeof err === "object" && !Array.isArray(err)) {
              Object.values(err).forEach((innerErr) => {
                if (Array.isArray(innerErr)) {
                  innerErr.forEach((message) =>
                    showToast(message, showToastType.error)
                  );
                } else {
                  showToast(innerErr, showToastType.error);
                }
              });
            } else {
              showToast(err, showToastType.error);
            }
          });
        } else if (typeof errors === "object") {
          Object.values(errors).forEach((err) => {
            if (Array.isArray(err)) {
              err.forEach((message) => showToast(message, showToastType.error));
            } else {
              showToast(err, showToastType.error);
            }
          });
        } else {
          showToast(errors, showToastType.error);
        }
      } else {
        showToast(error.message, showToastType.error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelButton = () => {
    navigate(ADMIN_ACCESS_USER);
  };

  const handleLockButtonClick = () => {
    const action = editUser.unlockUser;
    const message = editUser.errmsg.item1;

    showAlert(
      action,
      message,
      alertActions.warning,
      alertActions.Confirm,
      alertActions.Cancel
    ).then((result) => {
      if (result.isConfirmed) {
        handleLockAction();
      }
    });
  };

  const handleLockAction = async () => {
    try {
      const response = await privateAxios.put(
        getUnlockUserUrl,
        {},
        {
          params: {
            userAccountId: id,
            email: userData.primaryEmail
          }
        }
      );
      showToast(response.data.message, showToastType.success);
    } catch (error) {
      showToast(error.response.data.message, showToastType.error);
    }
  };

  if (!userData) return <p>{buttonName?.loading}</p>;

  return (
    <>
      <TablePagesTitle
        mainTitle={userManage.mainTitle}
        content={userManage.content}
      />
      <AccessPageAction
        subheading={editUser.editUserBtn}
        alluser={ADMIN_ACCESS_USER}
        allUserTitle={userManage.activeUser}
        createuser={CREATE_USER}
        createUserTitle={userManage.creteUser}
        deactivate={DEACTIVATED_USER}
        deactivateTitle={userManage.deactiveUserBtn}
        deleteuser={DELETED_USER}
        deleteUserTitle={userManage.deleteUser}
      />
      {loading ? (
        <EditUserSkeleton />
      ) : (
        <div className="row mb-3 mx-0">
          <div className="col-md-12 sale-leade-form mt-3">
            <form onSubmit={handleSubmit}>
              <div className="row mb-2">
                <div className="col-sm-4">
                  <div className="input-title">
                    <label htmlFor="firstName">{profiletexts.first_name}</label>
                  </div>
                </div>
                <div className="col-sm-8">
                  <div className="input-group">
                    <input
                      type="text"
                      placeholder="First Name"
                      name="firstName"
                      value={userData.firstName || ""}
                      onChange={handleInputChange}
                    />
                    {nameError && (
                      <div className="text-danger small">{nameError}</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-sm-4">
                  <div className="input-title">
                    <label htmlFor="middleName">
                      {profiletexts.middle_name}
                    </label>
                  </div>
                </div>
                <div className="col-sm-8">
                  <div className="input-group">
                    <input
                      type="text"
                      placeholder="Middle Name"
                      name="middleName"
                      value={userData.middleName || ""}
                      onChange={handleInputChange}
                    />
                    {middleNameError && (
                      <div className="text-danger small">{middleNameError}</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-sm-4">
                  <div className="input-title">
                    <label htmlFor="last_name">{profiletexts.last_name}</label>
                  </div>
                </div>
                <div className="col-sm-8">
                  <div className="input-group">
                    <input
                      type="text"
                      placeholder="Last Name"
                      name="lastName"
                      value={userData.lastName || ""}
                      onChange={handleInputChange}
                    />
                    {lastNameError && (
                      <div className="text-danger small">{lastNameError}</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-sm-4">
                  <div className="input-title">
                    <label htmlFor="mailAdress">{createUser.mailAdress}</label>
                  </div>
                </div>
                <div className="col-sm-8">
                  <div className="input-group">
                    <input
                      type="email"
                      placeholder="E-mail Address"
                      name="primaryEmail"
                      value={userData.primaryEmail || ""}
                      onChange={handleInputChange}
                    />
                    {emailError && (
                      <div className="text-danger small">{emailError}</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-4">
                  <div className="input-title">
                    <label htmlFor="activeDeactive">
                      {createUser.activeDeactive}
                    </label>
                  </div>
                </div>
                <div className="col-sm-8">
                  <div className="input-group">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={userData.isActive}
                      onChange={(e) =>
                        setUserData({ ...userData, isActive: e.target.checked })
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-sm-4">
                  <div className="input-title">
                    <label htmlFor="lockUnlock">{editUser.lockUnlock}</label>
                  </div>
                </div>
                <div className="col-sm-8">
                  <div className="input-group">
                    <button
                      type="button"
                      className={`lock btn btn-success btn-small`}
                      onClick={handleLockButtonClick}
                      data-tooltip-id="tooltip-EdituserMange"
                      data-tooltip-content={editUser.unlockUser}
                    >
                      <FaLockOpen />
                    </button>
                  </div>
                </div>
              </div>

              <div className="row mb-2">
                <div className="col-sm-4">
                  <div className="input-title">
                    <label htmlFor="associatedRoles">
                      {createUser.associatedRoles}
                    </label>
                  </div>
                </div>
                <div className="col-sm-8">
                  <div className="border p-3">
                    <div className="row role-list-wrap">
                      {roleLoading ? (
                        <EditUserRoleSkeleton />
                      ) : (
                        addroles?.map((role) => (
                          <div key={role.id} className="col-lg-6 col-xl-4">
                            <input
                              type="checkbox"
                              id={role.id}
                              checked={userData?.roles?.includes(role?.id)}
                              value={role.id}
                              onChange={() => handleCheckboxChange(role.id)}
                            />
                            <label className="ms-1" htmlFor={role.id}>
                              {role.name}
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  {rolesError && (
                    <div className="text-danger small">{rolesError}</div>
                  )}
                </div>
              </div>

              <div className="row mt-4">
                <div className="col-sm-8 offset-sm-4 d-flex d-sm-block justify-content-center justify-content-sm-start btn_grp">
                  <button
                    type="button"
                    className="btn btn-red me-2"
                    onClick={handleCancelButton}
                  >
                    {alertActions.Cancel}
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {buttonName.update}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
      <Tooltip id="tooltip-EdituserMange" className="small_tooltip" />
    </>
  );
};

export default EditUserManage;
